import { c as _c } from "react/compiler-runtime";
import type { Fetchable } from '@morpho-org/blue-sdk';
import type { InterfaceToken, Point, Rewards } from '@morpho-org/dapp-core';
import { HoverableCell } from '@morpho-org/ui-v6/client';
import { cn } from '@morpho-org/ui-v6/utils';
import { Icon } from '@repo/ui';
import { ArrowUpToLine, PointsGradient, RewardsGradient, WarningTriangleSolid } from '@repo/ui/icons';
import type { VaultV2ApyBreakdown } from '@/lib/procedures/vaultV2Apy.contract';
import { formatVaultApy } from '@/helpers/formatters';
import { getNetRateOnMarket } from '@/helpers/getNativeApy';
import { getVaultRowDisplayedNetApy, mapVaultRowApyCardRows } from '@/helpers/vaultApyCardRows';
import MerklRewardsHoverCard, { type MerklTarget } from '../../RewardsHoverCard/MerklRewardsHoverCard';
interface CellNetApyProps {
  netApy: Fetchable<bigint>;
  apy: Fetchable<bigint>;
  performanceFee: Fetchable<bigint>;
  managementFee?: bigint | null;
  rewards: Fetchable<Rewards[]>;
  variant: 'market' | 'vault' | 'supply';
  points?: Point[];
  /** text-* class controlling the text variant (default: `text-xs`). */
  textClassName?: string;
  isHoverable?: boolean;
  asset?: InterfaceToken | null;
  withBackgroundEffect?: boolean;
  durationText?: string;
  maxRate?: bigint | null;
  maxApy?: number | null;
  isRateCapped?: boolean;
  includesDonations?: boolean;
  merklContext?: MerklTarget | null;
  /** Set for V2 vaults (null when the split is unknown); undefined keeps the V1 card. */
  apyBreakdown?: VaultV2ApyBreakdown | null;
}
const getUpdatedNetApy = ({
  variant,
  netApy,
  apy,
  rewards
}: Pick<CellNetApyProps, 'variant' | 'netApy' | 'apy' | 'rewards'>) => variant === 'vault' ? netApy : getNetRateOnMarket({
  rewards,
  nativeRate: apy
});
const getCellNetApyState = ({
  apy,
  netApy,
  rewards,
  points,
  includesDonations,
  maxApy,
  isRateCapped,
  isHoverable,
  apyBreakdown
}: Pick<CellNetApyProps, 'apy' | 'netApy' | 'rewards' | 'points' | 'includesDonations' | 'maxApy' | 'isRateCapped' | 'isHoverable' | 'apyBreakdown'>) => {
  const isVaultV2 = apyBreakdown !== undefined;
  const rows = isVaultV2 ? mapVaultRowApyCardRows({
    breakdown: apyBreakdown,
    apy,
    netApy,
    rewards,
    includesDonations,
    isRateCapped,
    maxApy
  }) : null;
  const isPaused = rows?.hasPausedIcon ?? maxApy === 0;
  const hasRewardSparkle = rows?.hasSparkle ?? (!!rewards?.length || !!includesDonations);
  const hasCapIcon = rows?.hasCapIcon ?? (!!isRateCapped && !isPaused);
  const hasV2Card = (rows?.hasCard ?? false) || !!points?.length;
  return {
    isVaultV2,
    reconciledNetApy: getVaultRowDisplayedNetApy(rows, apyBreakdown),
    isPaused,
    hasRewardSparkle,
    hasCapIcon,
    isDisabled: !isHoverable || isVaultV2 && !hasV2Card
  };
};
const CellNetApy = t0 => {
  const $ = _c(57);
  const {
    netApy,
    apy,
    performanceFee,
    managementFee,
    rewards,
    variant,
    points,
    isHoverable: t1,
    textClassName: t2,
    asset,
    withBackgroundEffect: t3,
    durationText,
    maxRate,
    maxApy,
    isRateCapped,
    includesDonations,
    merklContext,
    apyBreakdown
  } = t0;
  const isHoverable = t1 === undefined ? true : t1;
  const textClassName = t2 === undefined ? "text-xs" : t2;
  const withBackgroundEffect = t3 === undefined ? true : t3;
  let hasCapIcon;
  let hasRewardSparkle;
  let isDisabled;
  let isPaused;
  let isVaultV2;
  let t4;
  let updatedNetApy;
  if ($[0] !== apy || $[1] !== apyBreakdown || $[2] !== includesDonations || $[3] !== isHoverable || $[4] !== isRateCapped || $[5] !== maxApy || $[6] !== netApy || $[7] !== points || $[8] !== rewards || $[9] !== variant) {
    const {
      isVaultV2: t5,
      reconciledNetApy,
      isPaused: t6,
      hasRewardSparkle: t7,
      hasCapIcon: t8,
      isDisabled: t9
    } = getCellNetApyState({
      apy,
      netApy,
      rewards,
      points,
      includesDonations,
      maxApy,
      isRateCapped,
      isHoverable,
      apyBreakdown
    });
    isVaultV2 = t5;
    isPaused = t6;
    hasRewardSparkle = t7;
    hasCapIcon = t8;
    isDisabled = t9;
    updatedNetApy = reconciledNetApy ?? getUpdatedNetApy({
      variant,
      netApy,
      apy,
      rewards
    });
    t4 = updatedNetApy == null ? null : formatVaultApy(updatedNetApy);
    $[0] = apy;
    $[1] = apyBreakdown;
    $[2] = includesDonations;
    $[3] = isHoverable;
    $[4] = isRateCapped;
    $[5] = maxApy;
    $[6] = netApy;
    $[7] = points;
    $[8] = rewards;
    $[9] = variant;
    $[10] = hasCapIcon;
    $[11] = hasRewardSparkle;
    $[12] = isDisabled;
    $[13] = isPaused;
    $[14] = isVaultV2;
    $[15] = t4;
    $[16] = updatedNetApy;
  } else {
    hasCapIcon = $[10];
    hasRewardSparkle = $[11];
    isDisabled = $[12];
    isPaused = $[13];
    isVaultV2 = $[14];
    t4 = $[15];
    updatedNetApy = $[16];
  }
  const formatNetApy = t4;
  if (!formatNetApy) {
    return null;
  }
  let t5;
  if ($[17] !== apy || $[18] !== apyBreakdown || $[19] !== asset || $[20] !== durationText || $[21] !== includesDonations || $[22] !== isRateCapped || $[23] !== managementFee || $[24] !== maxApy || $[25] !== maxRate || $[26] !== merklContext || $[27] !== performanceFee || $[28] !== points || $[29] !== rewards || $[30] !== updatedNetApy || $[31] !== variant) {
    t5 = <MerklRewardsHoverCard netApy={updatedNetApy} apy={apy} performanceFee={performanceFee} managementFee={managementFee} rewards={rewards} variant={variant} points={points} asset={asset} durationText={durationText} maxRate={maxRate} maxApy={maxApy} isRateCapped={isRateCapped} includesDonations={includesDonations} merklContext={merklContext} apyBreakdown={apyBreakdown} />;
    $[17] = apy;
    $[18] = apyBreakdown;
    $[19] = asset;
    $[20] = durationText;
    $[21] = includesDonations;
    $[22] = isRateCapped;
    $[23] = managementFee;
    $[24] = maxApy;
    $[25] = maxRate;
    $[26] = merklContext;
    $[27] = performanceFee;
    $[28] = points;
    $[29] = rewards;
    $[30] = updatedNetApy;
    $[31] = variant;
    $[32] = t5;
  } else {
    t5 = $[32];
  }
  let t6;
  if ($[33] !== textClassName) {
    t6 = cn(textClassName, "text-foreground");
    $[33] = textClassName;
    $[34] = t6;
  } else {
    t6 = $[34];
  }
  let t7;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      display: "flex",
      alignItems: "center",
      lineHeight: "normal"
    };
    $[35] = t7;
  } else {
    t7 = $[35];
  }
  let t8;
  if ($[36] !== formatNetApy || $[37] !== t6) {
    t8 = <span className={t6} style={t7}>{formatNetApy}</span>;
    $[36] = formatNetApy;
    $[37] = t6;
    $[38] = t8;
  } else {
    t8 = $[38];
  }
  let t9;
  if ($[39] !== hasRewardSparkle) {
    t9 = hasRewardSparkle ? <Icon icon={<RewardsGradient data-testid="cell-net-apy-reward-icon" />} /> : null;
    $[39] = hasRewardSparkle;
    $[40] = t9;
  } else {
    t9 = $[40];
  }
  let t10;
  if ($[41] !== hasCapIcon || $[42] !== isPaused || $[43] !== isVaultV2) {
    t10 = isVaultV2 && (hasCapIcon || isPaused) ? <Icon className={isPaused ? "text-warning-foreground" : "text-info-foreground"} icon={isPaused ? <WarningTriangleSolid data-testid="cell-net-apy-paused-icon" /> : <ArrowUpToLine data-testid="cell-net-apy-cap-icon" />} /> : null;
    $[41] = hasCapIcon;
    $[42] = isPaused;
    $[43] = isVaultV2;
    $[44] = t10;
  } else {
    t10 = $[44];
  }
  let t11;
  if ($[45] !== points?.length) {
    t11 = points?.length ? <Icon icon={<PointsGradient />} /> : null;
    $[45] = points?.length;
    $[46] = t11;
  } else {
    t11 = $[46];
  }
  let t12;
  if ($[47] !== t10 || $[48] !== t11 || $[49] !== t8 || $[50] !== t9) {
    t12 = <div className="flex items-center gap-[2px]">{t8}{t9}{t10}{t11}</div>;
    $[47] = t10;
    $[48] = t11;
    $[49] = t8;
    $[50] = t9;
    $[51] = t12;
  } else {
    t12 = $[51];
  }
  let t13;
  if ($[52] !== isDisabled || $[53] !== t12 || $[54] !== t5 || $[55] !== withBackgroundEffect) {
    t13 = <HoverableCell className="w-max shrink-0 grow-0" withBackgroundEffect={withBackgroundEffect} hoverCardContent={t5} isDisabled={isDisabled} isKeyboardTrigger={true} padding="0 11px 0 14px">{t12}</HoverableCell>;
    $[52] = isDisabled;
    $[53] = t12;
    $[54] = t5;
    $[55] = withBackgroundEffect;
    $[56] = t13;
  } else {
    t13 = $[56];
  }
  return t13;
};
export default CellNetApy;